import { createAsyncThunk } from "@reduxjs/toolkit";
import { buildUrl } from "../api";
import { BoschAxios } from "../../api/BoschAxios";
import UserService from "../../utils/UserService";
import { getUsersRequest } from "../../utils/userRequests";
import {
  CreateGroupParticipantPayload,
  DeleteGroupParticipantPayload,
  GetGroupsResponse,
  GetGroupParticipantsResponse,
  GetGroupRulesResponse,
  SetGroupAdministratorPayload,
  UpdateGroupRulesPayload,
} from "../../types/gruposResponsabilidade";

interface ThunkError {
  httpStatus?: number;
  messageStatus?: any;
  message: string;
}

interface HttpError {
  httpStatus: number;
  message: string;
}

function createClassificationApproverThunk(type: string, routeName: string) {
  return createAsyncThunk<any, any, { rejectValue: ThunkError }>(
    type,
    async (classificationId, { rejectWithValue }) => {
      try {
        const baseRequest = await buildUrl({ route: routeName });
        const token = UserService.getToken();

        const response = await BoschAxios(
          `${baseRequest.route}${classificationId}`,
          baseRequest.method,
          null,
          token
        );

        return response.data;
      } catch (error: any) {
        return rejectWithValue({
          httpStatus: error.response?.status,
          message: error.response?.data?.message,
        });
      }
    }
  );
}

function createThunk(
  type: string,
  routeName: string,
  useMultipart: boolean = false
) {
  return createAsyncThunk<any, any, { rejectValue: ThunkError }>(
    type,
    async (data, { rejectWithValue }) => {
      try {
        const { route, method } = await buildUrl({ route: routeName });
        const token = UserService.getToken();

        const config = useMultipart
          ? { headers: { "Content-Type": "multipart/form-data" } }
          : undefined;

        const response = await BoschAxios(route, method, data, token, config);

        return response.data;
      } catch (error: any) {
        return rejectWithValue({
          httpStatus: error.response?.status,
          message: error.response?.data?.message ?? "Erro desconhecido",
        });
      }
    }
  );
}

export const getSession = createThunk("getSession", "getSession");

export const getSessionMessageV2 = createThunk(
  "getSessionMessageV2",
  "getSessionMessageV2"
);

export const getSessionId = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("getSessionId", async (sessionId, { rejectWithValue }) => {
  try {
    let baseRequest = await buildUrl({ route: "getSessionId" });
    let token = UserService.getToken();
    const response = await BoschAxios(
      baseRequest.route,
      baseRequest.method,
      sessionId,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response.status,
      message: error.response.data.message,
    });
  }
});

export const uploadSessionImages = createAsyncThunk<
  any,
  FormData,
  { rejectValue: ThunkError }
>("uploadSessionImages", async (formData, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "getSession" });
    const token = UserService.getToken();

    const response = await BoschAxios(
      baseRequest.route,
      baseRequest.method,
      formData,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message: error.response?.data?.message,
    });
  }
});

export const getSessionRestart = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("getSessionRestart", async (sessionId, { rejectWithValue }) => {
  try {
    let baseRequest = await buildUrl({ route: "getSessionRestart" });
    let token = UserService.getToken();

    const response = await BoschAxios(
      `${baseRequest.route}${sessionId}/restart`,
      baseRequest.method,
      null,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response.status,
      message: error.response.data.message,
    });
  }
});

export const getSessionMessage = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("getSessionMessage", async (data, { rejectWithValue }) => {
  try {
    const { route, method } = await buildUrl({ route: "getSessionMessage" });
    const token = UserService.getToken();

    const response = await BoschAxios(route, method, data, token);

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message: error.response?.data?.message ?? "Erro desconhecido",
    });
  }
});

export const classificationsAprroverTax = createClassificationApproverThunk(
  "classificationsAprroverTax",
  "classificationsAprroverTax"
);

export const classificationsAprroverEng = createClassificationApproverThunk(
  "classificationsAprroverEng",
  "classificationsAprroverEng"
);

export const classificationsSuspend = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("classificationsSuspend", async (classificationId, { rejectWithValue }) => {
  try {
    let baseRequest = await buildUrl({ route: "classificationsSuspend" });
    let token = UserService.getToken();

    const response = await BoschAxios(
      `${baseRequest.route}${classificationId}`,
      baseRequest.method,
      null,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response.status,
      message: error.response.data.message,
    });
  }
});

export const ClassificationsMessage = createAsyncThunk<
  any,
  { id: string; body: any },
  { rejectValue: ThunkError }
>("ClassificationsMessage", async ({ id, body }, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "ClassificationsMessage" });
    const token = UserService.getToken();

    const response = await BoschAxios(
      `${baseRequest.route}${id}`,
      baseRequest.method,
      body,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message: error.response?.data?.message,
    });
  }
});

export const editNaladi = createAsyncThunk<
  any,
  { id: string; naladi: any },
  { rejectValue: ThunkError }
>("editNaladi", async ({ id, naladi }, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "editNaladi" });
    const token = UserService.getToken();

    const response = await BoschAxios(
      `${baseRequest.route}${id}?naladiCode=${naladi}`,
      baseRequest.method,
      null,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message: error.response?.data?.message,
    });
  }
});

export const getClassificationsMessage = createThunk(
  "getClassificationsMessage",
  "getClassificationsMessage"
);

export const getsessionFavoritePost = createThunk(
  "getsessionFavoritePost",
  "getsessionFavoritePost"
);

export const getDesFavorite = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("getDesFavorite", async (sessionId, { rejectWithValue }) => {
  try {
    let baseRequest = await buildUrl({ route: "getDesFavorite" });
    let token = UserService.getToken();
    const response = await BoschAxios(
      `${baseRequest.route}${sessionId}`,
      baseRequest.method,
      null,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response.status,
      message: error.response.data.message,
    });
  }
});

export const getSessionMetadata = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("getSessionMetadata", async (sessionId, { rejectWithValue }) => {
  let baseRequest = await buildUrl({ route: "getSessionMetadata" });
  let token = UserService.getToken();
  return BoschAxios(
    `${baseRequest.route}${sessionId}`,
    baseRequest.method,
    null,
    token
  );
});

export const getSessionQuestions = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("getSessionQuestions", async (sessionId, { rejectWithValue }) => {
  let baseRequest = await buildUrl({ route: "getSessionQuestions" });
  let token = UserService.getToken();
  return BoschAxios(
    `${baseRequest.route}${sessionId}`,
    baseRequest.method,
    null,
    token
  );
});

export const getProducts: any = createAsyncThunk(
  "getProducts",
  async (data: { id: string }) => {
    let baseRequest = await buildUrl({ route: "getProducts" });
    let token = UserService.getToken();
    return BoschAxios(
      `${baseRequest.route}?search=${data.id}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getFavorite: any = createAsyncThunk("getFavorite", async () => {
  let baseRequest = await buildUrl({ route: "getFavorite" });
  let token = UserService.getToken();
  return BoschAxios(`${baseRequest.route}`, baseRequest.method, null, token);
});

export const getClassifications: any = createAsyncThunk(
  "getClassifications",
  async (data: {
    status?: string;
    productCode?: string;
    presentationName?: string;
    emitter?: string;
    center?: string;
    startDate?: string;
    endDate?: string;
    classificationQuizStartDate?: string;
    classificationQuizFinishDate?: string;
    classificationApprovalStartDate?: string;
    classificationApprovalEndDate?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
    endDateTime?: string;
    startDateTime?: string;
  }) => {
    let baseRequest = await buildUrl({ route: "getClassifications" });
    let token = UserService.getToken();
    return BoschAxios(
      `${baseRequest.route}?status=${data.status}&productCode=${data.productCode}&emitter=${data.emitter}&presentationName=${data.presentationName}&startDate=${data.startDate}&endDate=${data.endDate}&classificationQuizStartDate=${data.classificationQuizStartDate}&classificationQuizFinishDate=${data.classificationQuizFinishDate}&classificationApprovalStartDate=${data.classificationApprovalStartDate}&classificationApprovalEndDate=${data.classificationApprovalEndDate}&page=${data.page}&size=${data.size}&sortBy=${data.sortBy}&sortDirection=${data.sortDirection}&center=${data.center}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getClassificationsTax: any = createAsyncThunk(
  "getClassificationsTax",
  async (data: {
    status?: string;
    flag?: string;
    ncmHarmonizationAlert?: string;
    center?: string;
    productCode?: string;
    presentationName?: string;
    emitter?: string;
    startDate?: string;
    endDate?: string;
    classificationQuizStartDate?: string;
    classificationQuizFinishDate?: string;
    classificationApprovalStartDate?: string;
    classificationApprovalEndDate?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDirection?: string;
    endDateTime?: string;
    startDateTime?: string;
  }) => {
    let baseRequest = await buildUrl({ route: "getClassificationsTax" });
    let token = UserService.getToken();

    let ncmHarmonizationAlert = "";

    if (typeof data.ncmHarmonizationAlert === "string") {
      const parts = data.ncmHarmonizationAlert.split(",").map((p) => p.trim());
      if (parts.length === 1) {
        ncmHarmonizationAlert = parts[0];
      }
    }

    return BoschAxios(
      `${baseRequest.route}?status=${data.status}&productCode=${data.productCode}&emitter=${data.emitter}&presentationName=${data.presentationName}&startDate=${data.startDate}&endDate=${data.endDate}&classificationQuizStartDate=${data.classificationQuizStartDate}&classificationQuizFinishDate=${data.classificationQuizFinishDate}&classificationApprovalStartDate=${data.classificationApprovalStartDate}&classificationApprovalEndDate=${data.classificationApprovalEndDate}&page=${data.page}&size=${data.size}&sortBy=${data.sortBy}&sortDirection=${data.sortDirection}&flag=${data.flag}&ncmHarmonizationAlert=${ncmHarmonizationAlert}&center=${data.center}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getDashboard: any = createAsyncThunk(
  "getDashboard",
  async (data: { emitter?: string; startDate?: string; endDate?: string }) => {
    const baseRequest = await buildUrl({ route: "getDashboard" });
    const token = UserService.getToken();

    let startDate = "";
    let endDate = "";
    let emitter = "";

    const formatDate = (dateStr: string): string => {
      const [day, month, year] = dateStr.split("-");
      return `${year}-${month}-${day}`;
    };

    if (data.startDate && data.startDate !== "Invalid date") {
      const formattedStart = formatDate(data.startDate);
      startDate = `startDate=${formattedStart}`;
    }

    if (data.endDate && data.endDate !== "Invalid date") {
      endDate = `&endDate=${formatDate(data.endDate)}`;
    }

    if (data.emitter) emitter = `&emitter=${data.emitter}`;
    return BoschAxios(
      `${baseRequest.route}?${startDate}${endDate}${emitter}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getClassificationsId: any = createAsyncThunk(
  "getClassificationsId",
  async (data: { id: string }) => {
    let baseRequest = await buildUrl({ route: "getClassificationsId" });
    let token = UserService.getToken();
    return BoschAxios(
      `${baseRequest.route}${data.id}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getClassificationsPn: any = createAsyncThunk(
  "getClassificationsPn",
  async (data: { pn: string; ct: string }) => {
    let baseRequest = await buildUrl({ route: "getClassificationsPn" });
    let token = UserService.getToken();
    let center = "";

    if (data?.ct) center = `?center=${data?.ct}`;
    return BoschAxios(
      `${baseRequest.route}${data.pn}${center}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getUserApprover = createAsyncThunk<
  any,
  { email?: string; page?: number },
  { rejectValue: HttpError }
>("getUserApprover", async (data, { rejectWithValue }) => {
  try {
    return await getUsersRequest("getUserApprover", data);
  } catch (err: any) {
    return rejectWithValue({
      httpStatus: err?.response?.status ?? 500,
      message: err?.message ?? "Erro ao buscar usuários aprovadores",
    });
  }
});



export const getUserEngInternals = createAsyncThunk<
  any,
  { email?: string; page?: number },
  { rejectValue: HttpError }
>("getUserEngInternals", async (data, { rejectWithValue }) => {
  try {
    return await getUsersRequest("getUserEngInternals", data);
  } catch (err: any) {
    return rejectWithValue({
      httpStatus: err?.response?.status ?? 500,
      message: err?.message ?? "Erro ao buscar usuários internos",
    });
  }
});

export const getClassificationHistory = createAsyncThunk<
  any,
  { classificationId: string; page?: number; size?: number },
  { rejectValue: HttpError }
>("getClassificationHistory", async (data, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "getClassificationHistory" });
    const token = UserService.getToken();

    const params = new URLSearchParams();
    if (data?.page !== undefined) params.append("page", String(data.page));
    if (data?.size !== undefined) params.append("size", String(data.size));

    const query = params.toString();
    const queryString = query ? `?${query}` : "";

    const url = `${baseRequest.route}${data.classificationId}${queryString}`;

    const response = await BoschAxios(url, baseRequest.method, null, token);
    return response?.message ?? response;
  } catch (err: any) {
    return rejectWithValue({
      httpStatus: err?.response?.status ?? 500,
      message: err?.message ?? "Erro ao buscar histórico da classificação",
    });
  }
});

export const getGroups = createAsyncThunk<
  GetGroupsResponse,
  { page?: number; pageSize?: number; size?: number },
  { rejectValue: HttpError }
>("getGroups", async (data, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "getGroups" });
    const token = UserService.getToken();

    const params = new URLSearchParams();
    if (data?.page !== undefined) params.append("page", String(data.page));
    const size = data?.size ?? data?.pageSize;
    if (size !== undefined) params.append("size", String(size));

    const query = params.toString();
    const url = query ? `${baseRequest.route}?${query}` : baseRequest.route;

    const response = await BoschAxios(url, baseRequest.method, null, token);
    return response?.payload ?? response;
  } catch (err: any) {
    return rejectWithValue({
      httpStatus: err?.response?.status ?? 500,
      message:
        err?.response?.data?.message ??
        err?.message ??
        "Erro ao buscar grupos de responsabilidade",
    });
  }
});

export const getGroupParticipants = createAsyncThunk<
  GetGroupParticipantsResponse,
  { groupId: string; page?: number; size?: number; email?: string },
  { rejectValue: ThunkError }
>(
  "getGroupParticipants",
  async ({ groupId, page, size, email }, { rejectWithValue }) => {
    try {
      const baseRequest = await buildUrl({ route: "getGroupParticipants" });
      const token = UserService.getToken();
      const params = new URLSearchParams();

      if (email) params.append("email", email);
      if (page !== undefined) params.append("page", String(page));
      if (size !== undefined) params.append("size", String(size));

      const query = params.toString();
      const queryString = query ? `?${query}` : "";
      const response = await BoschAxios(
        `${baseRequest.route}${groupId}/participants${queryString}`,
        baseRequest.method,
        null,
        token,
      );


      return response?.payload ?? response;
    } catch (error: any) {
      return rejectWithValue({
        httpStatus: error.response?.status,
        message: error.response?.data?.message ?? "Erro ao buscar participantes do grupo",
      });
    }
  },
);

export const createGroupParticipant = createAsyncThunk<
  any,
  CreateGroupParticipantPayload,
  { rejectValue: ThunkError }
>("createGroupParticipant", async ({ groupId, userId }, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "createGroupParticipant" });
    const token = UserService.getToken();
    const response = await BoschAxios(
      `${baseRequest.route}${groupId}/participants`,
      baseRequest.method,
      { userId },
      token,
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message: error.response?.data?.message ?? "Erro ao adicionar participante ao grupo",
    });
  }
});

export const deleteGroupParticipant = createAsyncThunk<
  any,
  DeleteGroupParticipantPayload,
  { rejectValue: ThunkError }
>(
  "deleteGroupParticipant",
  async ({ groupId, userId }, { rejectWithValue }) => {
    try {
      const baseRequest = await buildUrl({ route: "deleteGroupParticipant" });
      const token = UserService.getToken();
      const response = await BoschAxios(
        `${baseRequest.route}${groupId}/participants/${userId}`,
        baseRequest.method,
        null,
        token,
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        httpStatus: error.response?.status,
        message: error.response?.data?.message ?? "Erro ao remover participante do grupo",
      });
    }
  },
);

export const setGroupAdministrator = createAsyncThunk<
  any,
  SetGroupAdministratorPayload,
  { rejectValue: ThunkError }
>(
  "setGroupAdministrator",
  async ({ groupId, newAdministratorId }, { rejectWithValue }) => {
    try {
      const baseRequest = await buildUrl({ route: "setGroupAdministrator" });
      const token = UserService.getToken();
      const response = await BoschAxios(
        `${baseRequest.route}${groupId}/administrator`,
        baseRequest.method,
        { newAdministratorId },
        token,
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        httpStatus: error.response?.status,
        message: error.response?.data?.message ?? "Erro ao definir o novo administrador do grupo",
      });
    }
  },
);

export const getUserDelete: any = createAsyncThunk(
  "getUserDelete",
  async (data: { email: string; page?: number }) => {
    let baseRequest = await buildUrl({ route: "getUserDelete" });
    let token = UserService.getToken();
    let email = "";

    if (data?.email) email = `${data?.email}`;
    return BoschAxios(
      `${baseRequest.route}${email}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getUserEngDelete: any = createAsyncThunk(
  "getUserEngDelete",
  async (data: { id: string; page?: number }) => {
    let baseRequest = await buildUrl({ route: "getUserEngDelete" });
    let token = UserService.getToken();
    let id = "";

    if (data?.id) id = `${data?.id}`;
    return BoschAxios(
      `${baseRequest.route}${id}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getUserEdit: any = createAsyncThunk(
  "getUserEdit",
  async (data: { email: string }) => {
    let baseRequest = await buildUrl({ route: "getUserEdit" });
    let token = UserService.getToken();
    let email = "";

    if (data?.email) email = `?email=${data?.email}`;
    return BoschAxios(
      `${baseRequest.route}${email}`,
      baseRequest.method,
      null,
      token
    );
  }
);

interface PutEditPayload {
  email: string;
  crea: string;
}

export const getPutEdit = createAsyncThunk(
  "getPutEdit",
  async (data: PutEditPayload) => {
    const baseRequest = await buildUrl({ route: "getPutEdit" });
    const token = UserService.getToken();

    const emailParam = data?.email ? `${data.email}` : "";

    const body = {
      crea: data.crea
    };

    return BoschAxios(
      `${baseRequest.route}${emailParam}`,
      baseRequest.method,
      body,
      token
    );
  }
);

interface PutEditCreaEngPayload {
  crea: string;
}

export const getPutEditCreaEng = createAsyncThunk(
  "getPutEditCreaEng",
  async (data: PutEditCreaEngPayload) => {
    const baseRequest = await buildUrl({ route: "getPutEditCreaEng" });
    const token = UserService.getToken();

    const body = {
      crea: data.crea
    };

    return BoschAxios(
      `${baseRequest.route}`,
      baseRequest.method,
      body,
      token
    );
  }
);

interface PutUserEngInternals {
  engineersToBecomeAssociates: string[];
}

export const getPutUserEngInternals = createAsyncThunk(
  "getPutUserEngInternals",
  async (data: PutUserEngInternals) => {
    const baseRequest = await buildUrl({ route: "getPutUserEngInternals" });
    const token = UserService.getToken();

    const body = {
      engineersToBecomeAssociates: data.engineersToBecomeAssociates
    };

    return BoschAxios(
      `${baseRequest.route}`,
      baseRequest.method,
      body,
      token
    );
  }
);

interface CreateGroupPayload {
  name: string;
  engineerResponsibleId: string;
}

export const createGroup = createAsyncThunk<
  any,
  CreateGroupPayload,
  { rejectValue: ThunkError }
>("createGroup", async (data, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "createGroup" });
    const token = UserService.getToken();
    const response = await BoschAxios(
      baseRequest.route,
      baseRequest.method,
      data,
      token,
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message: error.response?.data?.message ?? "Erro desconhecido",
    });
  }
});

export const deleteGroup = createAsyncThunk<
  any,
  string,
  { rejectValue: ThunkError }
>("deleteGroup", async (groupId, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "deleteGroup" });
    const token = UserService.getToken();
    const response = await BoschAxios(
      `${baseRequest.route}${groupId}`,
      baseRequest.method,
      null,
      token,
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message: error.response?.data?.message ?? "Erro desconhecido",
    });
  }
});

export const getGroupRules = createAsyncThunk<
  GetGroupRulesResponse,
  string,
  { rejectValue: ThunkError }
>("getGroupRules", async (groupId, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "getGroupRules" });
    const token = UserService.getToken();
    const response = await BoschAxios(
      `${baseRequest.route}${groupId}/rules`,
      baseRequest.method,
      null,
      token,
    );

    return response?.payload ?? response;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message: error.response?.data?.message ?? "Erro ao buscar regras do grupo",
    });
  }
});

export const updateGroupRules = createAsyncThunk<
  any,
  UpdateGroupRulesPayload,
  { rejectValue: ThunkError }
>("updateGroupRules", async ({ groupId, rules }, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "updateGroupRules" });
    const token = UserService.getToken();
    const response = await BoschAxios(
      `${baseRequest.route}${groupId}/rules`,
      baseRequest.method,
      { rules },
      token,
    );

    return response?.data ?? response;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message:
        error.response?.data?.message ??
        error.message ??
        "Erro ao salvar regras do grupo",
    });
  }
});

export const getUserAutocomplete: any = createAsyncThunk(
  "getUserAutocomplete",
  async (data: { search: string }) => {
    let baseRequest = await buildUrl({ route: "getUserAutocomplete" });
    let token = UserService.getToken();
    let search = "";

    if (data?.search) search = `?search=${data?.search}`;
    return BoschAxios(
      `${baseRequest.route}${search}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getEngAdminAutocomplete: any = createAsyncThunk(
  "getEngAdminAutocomplete",
  async (data: { search: string }) => {
    let baseRequest = await buildUrl({ route: "getEngAdminAutocomplete" });
    let token = UserService.getToken();
    let search = "";

    if (data?.search) search = `?search=${data?.search}`;
    return BoschAxios(
      `${baseRequest.route}${search}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getSessionVerify: any = createAsyncThunk(
  "getSessionVerify",
  async (_, { rejectWithValue }) => {
    try {
      const baseRequest = await buildUrl({ route: "getSessionVerify" });
      const token = UserService.getToken();

      const response = await BoschAxios(
        baseRequest.route,
        baseRequest.method,
        null,
        token
      );

      // Normaliza para sempre retornar array
      const normalizedMessage = Array.isArray(response.message)
        ? response.message
        : [response.message];

      return {
        ...response,
        message: normalizedMessage,
      };
    } catch (error: any) {
      return rejectWithValue({
        message: error?.response?.data?.message || "Erro na requisição",
        httpStatus: error?.response?.status || null,
        messageStatus: error?.message,
      });
    }
  }
);

export const getSessionVerifyPn: any = createAsyncThunk(
  "getSessionVerifyPn",
  async (data: { pn: string; ct: string }) => {
    let baseRequest = await buildUrl({ route: "getSessionVerifyPn" });
    let token = UserService.getToken();
    let center = "";
    if (data?.ct) center = `?center=${data?.ct}`;

    if (data?.ct) {
      center = `?center=${data.ct}`;
      localStorage.setItem("selectedCenterForm", data.ct);
    }

    return BoschAxios(
      `${baseRequest.route}/${data.pn}${center}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getSessionResetVerifyPn: any = createAsyncThunk(
  "getSessionResetVerifyPn",
  async (data: { pn: string; ct: string }) => {
    let baseRequest = await buildUrl({ route: "getSessionResetVerifyPn" });
    let token = UserService.getToken();
    let center = "";
    if (data?.ct) center = `?center=${data?.ct}`;

    if (data?.ct) {
      center = `?center=${data.ct}`;
      localStorage.setItem("selectedCenterForm", data.ct);
    }

    return BoschAxios(
      `${baseRequest.route}/${data.pn}${center}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const getSessionResume = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("getSessionResume", async (sessionId, { rejectWithValue }) => {
  try {
    let baseRequest = await buildUrl({ route: "getSessionResume" });
    let token = UserService.getToken();

    const response = await BoschAxios(
      `${baseRequest.route}${sessionId}/resume`,
      baseRequest.method,
      null,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response.status,
      message: error.response.data.message,
    });
  }
});

export const getSessionFinalize = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("getSessionFinalize", async (sessionId, { rejectWithValue }) => {
  try {
    let baseRequest = await buildUrl({ route: "getSessionFinalize" });
    let token = UserService.getToken();

    const response = await BoschAxios(
      `${baseRequest.route}${sessionId}/finalize`,
      baseRequest.method,
      null,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response.status,
      message: error.response.data.message,
    });
  }
});

export const getCenter: any = createAsyncThunk(
  "getCenter",
  async (data: { id: string }) => {
    let baseRequest = await buildUrl({ route: "getCenter" });
    let token = UserService.getToken();
    return BoschAxios(
      `${baseRequest.route}?search=${data.id}`,
      baseRequest.method,
      null,
      token
    );
  }
);

export const classificationsTransfer = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("classificationsTransfer", async (classificationId, { rejectWithValue }) => {
  try {
    const baseRequest = await buildUrl({ route: "classificationsTransfer" });
    const token = UserService.getToken();

    const response = await BoschAxios(
      `${baseRequest.route}${classificationId}/transfer`,
      baseRequest.method,
      null,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response?.status,
      message: error.response?.data?.message,
    });
  }
});

export const getSessionReset = createAsyncThunk<
  any,
  any,
  { rejectValue: ThunkError }
>("getSessionReset", async (sessionId, { rejectWithValue }) => {
  try {
    let baseRequest = await buildUrl({ route: "getSessionReset" });
    let token = UserService.getToken();

    const response = await BoschAxios(
      `${baseRequest.route}${sessionId}/reset`,
      baseRequest.method,
      null,
      token
    );

    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      httpStatus: error.response.status,
      message: error.response.data.message,
    });
  }
});
